import React, { useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import * as QRCode from 'qrcode';

type Props = {
  tickets: any[];
  handleClose: () => void;
};

const QR_VERSION = '1.0.1';
const QR_EVENTO  = 'fatima';

const TICKET_W         = 80;   // mm (8cm)
const TICKET_H         = 50;   // mm (5cm)
const PAGE_MARGIN_X    = 19.5; // mm — centrado horizontal en tabloide (279 - 240) / 2
const PAGE_MARGIN_Y    = 16;   // mm — centrado vertical en tabloide (432 - 400) / 2
const GAP              = 0;    // mm — tickets pegados, separados solo por línea de corte
const TICKETS_PER_ROW  = 3;
const TICKETS_PER_PAGE = 24;   // 3 columnas × 8 filas

const ROWS = TICKETS_PER_PAGE / TICKETS_PER_ROW; // 8


const loadImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const drawCutLines = (doc: jsPDF) => {
  doc.setDrawColor(130, 130, 130);
  doc.setLineWidth(0.4);

  const blockW = TICKETS_PER_ROW * TICKET_W;
  const blockH = ROWS * TICKET_H;
  const x0 = PAGE_MARGIN_X;
  const y0 = PAGE_MARGIN_Y;
  const x1 = x0 + blockW;
  const y1 = y0 + blockH;

  // Líneas verticales: borde izq + entre columnas + borde der
  for (let j = 0; j <= TICKETS_PER_ROW; j++) {
    const xLine = x0 + j * TICKET_W;
    doc.line(xLine, y0, xLine, y1);
  }

  // Líneas horizontales: borde top + entre filas + borde bottom
  for (let i = 0; i <= ROWS; i++) {
    const yLine = y0 + i * TICKET_H;
    doc.line(x0, yLine, x1, yLine);
  }
};

const TicketsPdf: React.FC<Props> = ({ tickets, handleClose }) => {
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfError, setPdfError]     = useState<boolean>(false);
  const blobUrlRef                  = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const generatePDF = async (): Promise<string | null> => {
    if (!tickets.length) return null;

    const doc = new jsPDF({ format: 'tabloid', orientation: 'portrait' });
    const nuevoDisenoBase64 = await loadImageAsBase64(process.env.PUBLIC_URL + '/media/logos/El_Banco_de_Hoy.png');

    for (let i = 0; i < tickets.length; i++) {
      const ticket    = tickets[i];
      const posInPage = i % TICKETS_PER_PAGE;
      const row       = Math.floor(posInPage / TICKETS_PER_ROW);
      const col       = posInPage % TICKETS_PER_ROW;

      if (posInPage === 0 && i > 0) {
        drawCutLines(doc);
        doc.addPage();
      }

      const x = PAGE_MARGIN_X + col * (TICKET_W + GAP);
      const y = PAGE_MARGIN_Y + row * (TICKET_H + GAP);

      // Imagen de fondo completa del ticket
      if (nuevoDisenoBase64) {
        doc.addImage(nuevoDisenoBase64, 'PNG', x, y, TICKET_W, TICKET_H);
      }

      // --- QR en el recuadro blanco (derecha) ---
      const qrData = JSON.stringify({
        ticketcash: ticket.UUID,
        folio: ticket.folio,
        version: QR_VERSION,
        evento: QR_EVENTO,
      });
      const qrDataUrl = await QRCode.toDataURL(qrData, {
        margin: 1,
        width: 150,
        color: { dark: '#000000', light: '#ffffff' },
      });

      const qrSize = 27;
      const qrX    = x + 49;
      const qrY    = y + 10.2;
      doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

      // --- Folio sin fondo de color ---
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(
        `Folio:${ticket.folio ?? ''}`,
        x + 62.5,
        y + 43,
        { align: 'center' }
      );
    }

    // Dibujar líneas de corte encima de los tickets de la última página
    drawCutLines(doc);

    const blob = doc.output('blob');
    const url  = URL.createObjectURL(blob);
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    blobUrlRef.current = url;
    return url;
  };

  useEffect(() => {
    if (tickets.length === 0) return;
    setPdfError(false);
    generatePDF()
      .then((url) => {
        if (url) setPdfBlobUrl(url);
        else setPdfError(true);
      })
      .catch((err) => {
        console.error('Error generando PDF de tickets:', err);
        setPdfError(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);

  return (
    <div className="w-100">
      {pdfError ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
          <span className="text-danger text-center">No se pudo generar el PDF. Intenta de nuevo.</span>
        </div>
      ) : pdfBlobUrl ? (
        <iframe
          src={pdfBlobUrl}
          title="Vista previa — Tickets con QR"
          width="100%"
          height="620px"
          style={{ border: 'none', display: 'block' }}
        />
      ) : (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner-border text-primary mb-3" role="status" />
            <div className="text-muted fs-6">Generando PDF con tickets...</div>
          </div>
        </div>
      )}
      <div className="d-flex justify-content-end p-3">
        <button onClick={handleClose} className="btn btn-secondary">
          Cerrar
        </button>
      </div>
    </div>
  );
};

export { TicketsPdf };
